import type {
  ModifiedPlayer,
  NFLPlayerInputs,
  ProvidedOverride,
  RequestSimulationRunResponse,
  SimulationLineAnalyzerColumns,
  SimulationRun,
  SimulationStatus
} from '@library/types/simulations';
import { ascend, clone, partition, prop, sortWith } from 'ramda';
import type { ComboboxOption } from '@library/components/selects/combobox/Combobox';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { LeagueTeamPlayer } from '@library/types/player';
import type { LineAnalysis } from '@bo-components/simulations/steps/StepTwoResults/LineAnalyzerRow';
import { PLAYER_OVERRIDE_TYPES } from '@bo-test/mock/data/simulations';
import type { ProjectionsAndScoresAPIResponse } from '@library/types/projection';
import type { Sort } from '@library/types/utils';
import { getPlayerModalProjections } from '@bo-utils/playerModal';

export const determineInitialSimulationStep = (
  simulationData?: SimulationRun
) => {
  if (!simulationData) {
    return 1;
  }

  if (
    (simulationData.data.attributes.simulation_run_status ===
      'SIMULATION_IN_PROGRESS' ||
      simulationData.data.attributes.simulation_run_status ===
        'SIMULATION_FAILED' ||
      simulationData.data.attributes.simulation_run_status ===
        'SIMULATION_COMPLETED') &&
    simulationData.data.attributes.simulation_run_type === 'CANDIDATE'
  ) {
    return 2;
  }

  if (
    simulationData.data.attributes.simulation_run_status ===
      'PUBLICATION_IN_PROGRESS' ||
    simulationData.data.attributes.simulation_run_status ===
      'PUBLICATION_FAILED' ||
    simulationData.data.attributes.simulation_run_status ===
      'PUBLICATION_COMPLETED'
  ) {
    return 4;
  }

  return 1;
};

export const determineStepStatus = ({
  callingStep,
  currentStep
}: {
  callingStep: number;
  currentStep?: number;
}) => {
  if (!currentStep) {
    return 'pending';
  }

  if (currentStep === callingStep) {
    return 'current';
  }

  if (currentStep > callingStep) {
    return 'complete';
  }

  return 'pending';
};

export const organizeExistingProjectionsByMarket = ({
  projections
}: {
  allMarkets: Array<{
    league_id: string;
    market: string;
    market_id: string;
    stat_type_id: number;
  }>;
  projections: ProjectionsAndScoresAPIResponse;
}): {
  existingProjections: Array<GamePlayerProjectionTrading>;
} => {
  if (!projections) {
    return { existingProjections: [] };
  }

  const projectionsArray = getPlayerModalProjections(projections);

  return { existingProjections: projectionsArray };
};

export const sortLineAnalyzer = ({
  lineAnalyses,
  sort
}: {
  lineAnalyses: Array<LineAnalysis>;
  sort: Sort<SimulationLineAnalyzerColumns>;
}) => {
  return lineAnalyses.sort((lineAnalysisA, lineAnalysisB) => {
    /* if (sort.sortBy === 'player_id' && sort.sortDirection === 'asc') {
      return String(lineAnalysisA.playerId).localeCompare(
        String(lineAnalysisB.playerId)
      );
    }

    if (sort.sortBy === 'player_id' && sort.sortDirection === 'desc') {
      return String(lineAnalysisB.playerId).localeCompare(
        String(lineAnalysisA.playerId)
      );
    } */

    if (sort.sortBy === 'sport_radar_id' && sort.sortDirection === 'asc') {
      return String(lineAnalysisA.sportRadarId).localeCompare(
        String(lineAnalysisB.sportRadarId)
      );
    }

    if (sort.sortBy === 'sport_radar_id' && sort.sortDirection === 'desc') {
      return String(lineAnalysisB.sportRadarId).localeCompare(
        String(lineAnalysisA.sportRadarId)
      );
    }

    if (sort.sortBy === 'player_name' && sort.sortDirection === 'asc') {
      return String(lineAnalysisA.playerName).localeCompare(
        String(lineAnalysisB.playerName)
      );
    }

    if (sort.sortBy === 'player_name' && sort.sortDirection === 'desc') {
      return String(lineAnalysisB.playerName).localeCompare(
        String(lineAnalysisA.playerName)
      );
    }

    if (sort.sortBy === 'team' && sort.sortDirection === 'asc') {
      return String(lineAnalysisA.team).localeCompare(
        String(lineAnalysisB.team)
      );
    }

    if (sort.sortBy === 'team' && sort.sortDirection === 'desc') {
      return String(lineAnalysisB.team).localeCompare(
        String(lineAnalysisA.team)
      );
    }

    if (sort.sortBy === 'stat' && sort.sortDirection === 'asc') {
      return String(lineAnalysisA.stat).localeCompare(
        String(lineAnalysisB.stat)
      );
    }

    if (sort.sortBy === 'stat' && sort.sortDirection === 'desc') {
      return String(lineAnalysisB.stat).localeCompare(
        String(lineAnalysisA.stat)
      );
    }

    if (sort.sortBy === 'line_type' && sort.sortDirection === 'asc') {
      return String(lineAnalysisA.lineType).localeCompare(
        String(lineAnalysisB.lineType)
      );
    }

    if (sort.sortBy === 'line_type' && sort.sortDirection === 'desc') {
      return String(lineAnalysisB.lineType).localeCompare(
        String(lineAnalysisA.lineType)
      );
    }

    if (sort.sortBy === 'line' && sort.sortDirection === 'asc') {
      return Number(lineAnalysisA.line) - Number(lineAnalysisB.line);
    }

    if (sort.sortBy === 'line' && sort.sortDirection === 'desc') {
      return Number(lineAnalysisB.line) - Number(lineAnalysisA.line);
    }

    return 0;
  });
};

export const translateSimStatus = (status: SimulationStatus) => {
  switch (status) {
    case 'SIMULATION_IN_PROGRESS':
      return 'Simulation In Progress';
    case 'SIMULATION_FAILED':
      return 'Simulation Failed';
    case 'SIMULATION_COMPLETED':
      return 'Simulation Completed';
    case 'PUBLICATION_IN_PROGRESS':
      return 'Publication In Progress';
    case 'PUBLICATION_FAILED':
      return 'Publication Failed';
    case 'PUBLICATION_COMPLETED':
      return 'Publication Completed';
    default:
      return status;
  }
};

export const getSimSortValue = (sim: RequestSimulationRunResponse): number => {
  if (sim.attributes.simulation_run_type === 'PUBLISHED') {
    return 1;
  }

  if (sim.attributes.simulation_run_type === 'CANDIDATE') {
    return 2;
  }

  if (sim.attributes.simulation_run_status === 'PUBLICATION_COMPLETED') {
    return 3;
  }

  if (sim.attributes.simulation_run_status === 'PUBLICATION_IN_PROGRESS') {
    return 4;
  }

  if (sim.attributes.simulation_run_status === 'PUBLICATION_FAILED') {
    return 5;
  }

  if (sim.attributes.simulation_run_status === 'SIMULATION_COMPLETED') {
    return 6;
  }

  if (sim.attributes.simulation_run_status === 'SIMULATION_IN_PROGRESS') {
    return 7;
  }

  if (sim.attributes.simulation_run_status === 'SIMULATION_FAILED') {
    return 8;
  }

  return 9;
};

export const getPlayerSortValue = (player: LeagueTeamPlayer): number => {
  if (player.position === 'QB') {
    return 1;
  }

  if (player.position === 'RB') {
    return 2;
  }

  if (player.position === 'WR') {
    return 3;
  }

  if (player.position === 'TE') {
    return 4;
  }

  return 9;
};

export const isRelevantPosition = (player: LeagueTeamPlayer): boolean => {
  const { position } = player;

  return (
    position === 'QB' ||
    position === 'RB' ||
    position === 'WR' ||
    position === 'TE'
  );
};

export const createPlayerOptions = ({
  players
}: {
  players: Array<Omit<ModifiedPlayer, 'playerOverrides'>>;
}): Array<ComboboxOption> => {
  if (!players.length) {
    return [];
  }

  const teamPlayersOptions = players.map(({ foreignId, playerName }) => ({
    label: playerName,
    value: foreignId
  }));

  return teamPlayersOptions;
};

export const getSimulationNameHeaderText = ({
  isNewSimulation,
  simulationName,
  simulationType
}: {
  isNewSimulation: boolean;
  simulationName: string;
  simulationType?: 'CANDIDATE' | 'PUBLISHED';
}) => {
  if (isNewSimulation) {
    return 'NEW SIM';
  }

  if (simulationType === 'PUBLISHED') {
    return `PUBLISHED SIM - ${simulationName}`;
  }
  if (simulationType === 'CANDIDATE') {
    return `CANDIDATE SIM - ${simulationName}`;
  }

  return `EXISTING SIM - ${simulationName}`;
};

export const formatAmericanOdds = (americanOdds?: number): string => {
  if (americanOdds === undefined) {
    return '';
  }

  return americanOdds > 0 ? `+${americanOdds}` : String(americanOdds);
};

// since this was the first ask, I will put it here til we get a definite no for this
export const filterDefaultPlayerInputsByPosition = ({
  player
}: {
  player: LeagueTeamPlayer;
}): [
  ProvidedOverride<keyof NFLPlayerInputs>[],
  ProvidedOverride<keyof NFLPlayerInputs>[]
] => {
  return partition((override) => {
    const { position } = player;

    if (position === 'QB') {
      return (
        override.value === 'is_qb' ||
        override.value === 'pass_attempt_share' ||
        override.value === 'carry_share' ||
        override.value === 'rush_td_adjustment'
      );
    }

    if (position === 'RB') {
      return (
        override.value === 'carry_share' ||
        override.value === 'short_yardage_carry_share' ||
        override.value === 'rush_td_adjustment' ||
        override.value === 'rec_td_adjustment' ||
        override.value === 'target_adjustment' ||
        override.value === 'pass_play_playing_time' ||
        override.value === 'rush_play_playing_time'
      );
    }

    if (position === 'WR' || position === 'TE') {
      return (
        override.value === 'catch_adjustment' ||
        override.value === 'target_adjustment' ||
        override.value === 'rec_td_adjustment' ||
        override.value === 'pass_play_playing_time'
      );
    }

    return false;
  }, PLAYER_OVERRIDE_TYPES);
};

export const sortPlayersByPositionAndName = ({
  players
}: {
  players: Array<LeagueTeamPlayer>;
}) => {
  const positionNameSort = sortWith([
    (a: LeagueTeamPlayer, b: LeagueTeamPlayer) =>
      getPlayerSortValue(a) - getPlayerSortValue(b),
    ascend(prop('name'))
  ]);

  return positionNameSort(players);
};

export const filterPlayersByDefault = ({
  defaultSimulation,
  players
}: {
  defaultSimulation?: SimulationRun;
  players: Array<LeagueTeamPlayer>;
}) => {
  const teamAInputs =
    defaultSimulation?.data.attributes.simulation_run_inputs.player_inputs?.[0]
      ?.players;
  const teamBInputs =
    defaultSimulation?.data.attributes.simulation_run_inputs.player_inputs?.[1]
      ?.players;
  const teamARemoved =
    defaultSimulation?.data.attributes.simulation_run_inputs
      .removed_players?.[0]?.players;
  const teamBRemoved =
    defaultSimulation?.data.attributes.simulation_run_inputs
      .removed_players?.[0]?.players;

  const playersInDefault = players.filter(
    (player) =>
      teamAInputs?.find(
        (teamAPlayer) =>
          player.name.toLocaleUpperCase() ===
          teamAPlayer.player_name.toLocaleUpperCase()
      ) ||
      teamBInputs?.find(
        (teamBPlayer) =>
          player.name.toLocaleUpperCase() ===
          teamBPlayer.player_name.toLocaleUpperCase()
      ) ||
      teamARemoved?.find(
        (teamBPlayer) =>
          player.name.toLocaleUpperCase() ===
          teamBPlayer.player_name.toLocaleUpperCase()
      ) ||
      teamBRemoved?.find(
        (teamBPlayer) =>
          player.name.toLocaleUpperCase() ===
          teamBPlayer.player_name.toLocaleUpperCase()
      )
  );

  return playersInDefault;
};

export const transformToDisplayData = (
  defaultSimulation: SimulationRun | undefined
) => {
  if (!defaultSimulation) return undefined;

  const updatePlayer = (player: NFLPlayerInputs): NFLPlayerInputs => ({
    ...player,
    pass_attempt_share: player.pass_attempt_share * 100,
    carry_share: player.carry_share * 100,
    catch_adjustment: player.catch_adjustment * 100,
    short_yardage_carry_share: player.short_yardage_carry_share * 100,
    rush_td_rate: player.rush_td_rate * 100,
    rush_td_adjustment: player.rush_td_adjustment * 100,
    target_rate: player.target_rate * 100,
    target_adjustment: player.target_adjustment * 100,
    catch_rate: player.catch_rate * 100,
    rec_td_rate: player.rec_td_rate * 100,
    rec_td_adjustment: player.rec_td_adjustment * 100,
    pass_play_playing_time: player.pass_play_playing_time * 100,
    rush_play_playing_time: player.rush_play_playing_time * 100,
    pass_play_blowout_playing_time: player.pass_play_blowout_playing_time * 100,
    rush_play_blowout_playing_time: player.rush_play_blowout_playing_time * 100,
    route_run_rate: player.route_run_rate * 100
  });

  const updatedDefaultSimulationRun = clone(defaultSimulation);

  updatedDefaultSimulationRun.data.attributes.simulation_run_inputs.player_inputs =
    updatedDefaultSimulationRun.data.attributes.simulation_run_inputs.player_inputs.map(
      (team) => ({
        ...team,
        players: team.players.map((player) => updatePlayer(player))
      })
    );

  updatedDefaultSimulationRun.data.attributes.simulation_run_inputs.team_inputs =
    updatedDefaultSimulationRun.data.attributes.simulation_run_inputs.team_inputs.map(
      (team) => ({
        ...team,
        pass_attempt_rate: team.pass_attempt_rate * 100,
        pass_completion_rate: team.pass_completion_rate * 100,
        pass_play_prob_adjustment: team.pass_play_prob_adjustment * 100,
        pass_completion_prob_adjustment:
          team.pass_completion_prob_adjustment * 100,
        rush_touchdown_prob_adjustment:
          team.rush_touchdown_prob_adjustment * 100,
        pass_touchdown_prob_adjustment:
          team.pass_touchdown_prob_adjustment * 100,
        sack_rate: team.sack_rate * 100
      })
    );

  return updatedDefaultSimulationRun;
};

export const withinMinMax = ({
  currentValue,
  minimum,
  maximum
}: {
  currentValue: number | string;
  minimum: number | undefined;
  maximum: number | undefined;
}) =>
  typeof currentValue === 'number' &&
  ((minimum !== undefined && currentValue < minimum) ||
    (maximum !== undefined && currentValue > maximum));
